import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Smile, Meh, Frown, ChevronDown, ChevronUp, Tag, Clock, X, MessageSquare, ArrowRight, Phone } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { analyticsAPI } from '../../../services/api';

const GraficoSentimento = ({ metricas, loading, filtros }) => {
  const { darkMode } = useTheme();
  const [sentimentoAberto, setSentimentoAberto] = useState(null);
  const [conversas, setConversas] = useState([]);
  const [loadingConversas, setLoadingConversas] = useState(false);
  const [expandido, setExpandido] = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const clienteId = user.cliente_id;

  const carregarConversas = async (sentimento) => {
    if (sentimentoAberto === sentimento) { setSentimentoAberto(null); return; }
    setSentimentoAberto(sentimento);
    setExpandido(null);
    setLoadingConversas(true);
    try {
      const params = { sentimento };
      if (filtros?.dataInicio) params.dataInicio = filtros.dataInicio;
      if (filtros?.dataFim) params.dataFim = filtros.dataFim;
      if (filtros?.instanciaId) params.instanciaId = filtros.instanciaId;
      const res = await analyticsAPI.getConversasSentimento(clienteId, params);
      setConversas(res.data);
    } catch (e) {
      console.error('Erro ao carregar conversas:', e);
      setConversas([]);
    } finally { setLoadingConversas(false); }
  };

  useEffect(() => { setSentimentoAberto(null); }, [metricas]);

  if (loading) {
    return (
      <div className={`p-4 sm:p-6 rounded-xl animate-pulse ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
        <div className={`h-5 ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'} rounded w-1/4 mb-6`}></div>
        <div className={`h-72 ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'} rounded`}></div>
      </div>
    );
  }

  if (!metricas || metricas.length === 0) {
    return (
      <div className={`p-4 sm:p-6 rounded-xl border ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h3 className={`text-base sm:text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Análise de Sentimento</h3>
        <p className={`text-center py-10 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Nenhum dado de sentimento disponível</p>
      </div>
    );
  }

  const totalPositivo = metricas.reduce((sum, m) => sum + (m.sentimento_positivo || 0), 0);
  const totalNeutro = metricas.reduce((sum, m) => sum + (m.sentimento_neutro || 0), 0);
  const totalNegativo = metricas.reduce((sum, m) => sum + (m.sentimento_negativo || 0), 0);
  const total = totalPositivo + totalNeutro + totalNegativo;

  if (total === 0) {
    return (
      <div className={`p-4 sm:p-6 rounded-xl border ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h3 className={`text-base sm:text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Análise de Sentimento</h3>
        <p className={`text-center py-10 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Aguardando processamento de sentimento...</p>
      </div>
    );
  }

  const data = [
    { name: 'Positivo', value: totalPositivo, color: '#10b981' },
    { name: 'Neutro', value: totalNeutro, color: '#6b7280' },
    { name: 'Negativo', value: totalNegativo, color: '#ef4444' },
  ].filter(item => item.value > 0);

  const cards = [
    { key: 'positivo', label: 'Positivo', total: totalPositivo, Icon: Smile, color: 'green', bg: darkMode ? 'bg-green-900/20' : 'bg-green-50', ring: 'ring-green-500' },
    { key: 'neutro', label: 'Neutro', total: totalNeutro, Icon: Meh, color: 'gray', bg: darkMode ? 'bg-gray-700/50' : 'bg-gray-100', ring: 'ring-gray-500' },
    { key: 'negativo', label: 'Negativo', total: totalNegativo, Icon: Frown, color: 'red', bg: darkMode ? 'bg-red-900/20' : 'bg-red-50', ring: 'ring-red-500' },
  ];

  const corTexto = { green: 'text-green-500', gray: 'text-gray-500', red: 'text-red-500' };
  const sentimentoBorder = { positivo: 'border-green-500', neutro: 'border-gray-400', negativo: 'border-red-500' };

  const formatDate = (d) => new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <div className={`p-4 sm:p-6 rounded-xl border ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      <h3 className={`text-base sm:text-lg font-semibold mb-3 sm:mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        Análise de Sentimento
      </h3>

      {/* Gráfico + Cards lado a lado */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8 items-center">
        {/* Gráfico de Pizza */}
        <div className="lg:col-span-1">
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie data={data} cx="50%" cy="50%" labelLine={false}
                label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                outerRadius={65} dataKey="value">
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={{
                backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                borderRadius: '8px', color: darkMode ? '#ffffff' : '#000000',
              }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Cards clicáveis */}
        <div className="lg:col-span-2 grid grid-cols-3 gap-2 sm:gap-4">
          {cards.map(({ key, label, total: t, Icon, color, bg, ring }) => (
            <div key={key}
              onClick={() => t > 0 && carregarConversas(key)}
              className={`p-3 sm:p-5 rounded-xl ${bg} ${t > 0 ? 'cursor-pointer hover:scale-[1.02]' : 'opacity-50'} transition-all duration-200 ${sentimentoAberto === key ? `ring-2 ring-offset-2 ${ring} ${darkMode ? 'ring-offset-zinc-800' : 'ring-offset-white'}` : ''}`}
            >
              <div className="flex flex-col items-center text-center gap-1 sm:gap-2">
                <Icon className={`w-6 h-6 sm:w-8 sm:h-8 ${corTexto[color]}`} />
                <p className={`text-xl sm:text-3xl font-bold ${corTexto[color]}`}>{t}</p>
                <p className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{label}</p>
                <p className={`text-[10px] sm:text-xs ${corTexto[color]} opacity-70`}>{((t / total) * 100).toFixed(1)}%</p>
                {t > 0 && (
                  <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {sentimentoAberto === key ? 'Clique para fechar' : 'Clique para detalhes'}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Painel de detalhes expandido */}
      {sentimentoAberto && (
        <div className={`mt-8 border-t pt-6 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between mb-5">
            <h4 className={`text-base font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <MessageSquare className="w-5 h-5" />
              Mensagens — {sentimentoAberto.charAt(0).toUpperCase() + sentimentoAberto.slice(1)}
              <span className={`text-xs font-normal px-2.5 py-1 rounded-full ${darkMode ? 'bg-zinc-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                {conversas.length} resultado{conversas.length !== 1 ? 's' : ''}
              </span>
            </h4>
            <button onClick={() => setSentimentoAberto(null)}
              className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
              <X className="w-5 h-5" />
            </button>
          </div>

          {loadingConversas ? (
            <div className="space-y-4">
              {[1,2,3].map(i => (
                <div key={i} className={`h-28 rounded-xl animate-pulse ${darkMode ? 'bg-zinc-700' : 'bg-gray-100'}`} />
              ))}
            </div>
          ) : conversas.length === 0 ? (
            <p className={`text-center py-10 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Nenhuma conversa encontrada neste período
            </p>
          ) : (
            <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
              {conversas.map((c) => {
                const isExp = expandido === c.id;
                const ctxAntes = c.contexto?.find(x => x.pos === 'antes');
                const ctxDepois = c.contexto?.find(x => x.pos === 'depois');

                return (
                  <div key={c.id}
                    className={`rounded-xl border-l-4 ${sentimentoBorder[c.sentimento]} ${darkMode ? 'bg-zinc-700/40' : 'bg-gray-50'} transition-all`}>

                    {/* Card principal */}
                    <div className="p-5 cursor-pointer" onClick={() => setExpandido(isExp ? null : c.id)}>
                      {/* Contato + instância */}
                      <div className="flex items-center gap-3 flex-wrap mb-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${darkMode ? 'bg-zinc-600 text-white' : 'bg-gray-200 text-gray-700'}`}>
                          {(c.nome_contato || c.telefone || '?').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className={`font-semibold leading-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {c.nome_contato || c.telefone}
                          </p>
                          <p className={`flex items-center gap-1 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            <Phone className="w-3 h-3" />{c.telefone}
                          </p>
                        </div>
                        {c.instancia_nome && (
                          <span className={`text-xs px-2 py-1 rounded-lg ${darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                            📱 {c.instancia_nome}
                          </span>
                        )}
                        <span className={`ml-auto flex items-center gap-1 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          <Clock className="w-3.5 h-3.5" />{formatDate(c.data)}
                        </span>
                      </div>

                      {/* Mensagem do cliente */}
                      <div className={`p-3 rounded-lg mb-2 ${darkMode ? 'bg-zinc-800' : 'bg-white'} border ${darkMode ? 'border-zinc-600' : 'border-gray-200'}`}>
                        <p className={`text-xs mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>📩 Cliente:</p>
                        <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{c.mensagem}</p>
                      </div>

                      {/* Resposta */}
                      {c.resposta && (
                        <div className={`p-3 rounded-lg mb-2 ml-6 ${darkMode ? 'bg-blue-900/15 border-blue-900/30' : 'bg-blue-50 border-blue-100'} border`}>
                          <p className={`text-xs mb-1 ${darkMode ? 'text-blue-400' : 'text-blue-500'}`}>↩️ Resposta:</p>
                          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{c.resposta}</p>
                        </div>
                      )}

                      {/* Tags */}
                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                        {c.score != null && (
                          <span className={`text-xs px-2 py-1 rounded-lg font-medium ${
                            Number(c.score) > 0 ? (darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700') :
                            Number(c.score) < 0 ? (darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700') :
                            (darkMode ? 'bg-zinc-700 text-gray-400' : 'bg-gray-100 text-gray-600')
                          }`}>
                            Score: {Number(c.score).toFixed(2)}
                          </span>
                        )}
                        {c.categoria && c.categoria !== 'outro' && (
                          <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg ${darkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-50 text-purple-600'}`}>
                            <Tag className="w-3 h-3" />{c.categoria}
                          </span>
                        )}
                        {c.palavras_chave && (
                          <span className={`text-xs px-2 py-1 rounded-lg ${darkMode ? 'bg-zinc-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                            🔑 {c.palavras_chave}
                          </span>
                        )}
                        <span className={`ml-auto text-xs ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                          {isExp ? '▲ Fechar contexto' : '▼ Ver contexto'}
                        </span>
                      </div>
                    </div>

                    {/* Contexto expandido */}
                    {isExp && (
                      <div className={`mx-5 mb-5 p-4 rounded-lg ${darkMode ? 'bg-zinc-800/80' : 'bg-white'} border ${darkMode ? 'border-zinc-600' : 'border-gray-200'}`}>
                        <p className={`text-xs font-semibold mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          📋 Contexto da conversa
                        </p>

                        <div className="space-y-3">
                          {/* Antes */}
                          {ctxAntes ? (
                            <div className={`p-3 rounded-lg border-l-2 border-gray-400 ${darkMode ? 'bg-zinc-700/50' : 'bg-gray-50'}`}>
                              <p className={`text-xs mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                ⬆️ Mensagem anterior · {formatDate(ctxAntes.data)}
                              </p>
                              {ctxAntes.msg && <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>📩 {ctxAntes.msg}</p>}
                              {ctxAntes.resp && <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>↩️ {ctxAntes.resp}</p>}
                            </div>
                          ) : (
                            <p className={`text-xs py-2 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}>⬆️ Início da conversa</p>
                          )}

                          {/* Atual */}
                          <div className={`p-3 rounded-lg border-l-2 ${sentimentoBorder[c.sentimento]} ${
                            c.sentimento === 'positivo' ? (darkMode ? 'bg-green-900/15' : 'bg-green-50') :
                            c.sentimento === 'negativo' ? (darkMode ? 'bg-red-900/15' : 'bg-red-50') :
                            (darkMode ? 'bg-zinc-700/50' : 'bg-gray-50')
                          }`}>
                            <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              ▶️ Esta mensagem
                            </p>
                            <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>📩 {c.mensagem}</p>
                            {c.resposta && <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>↩️ {c.resposta}</p>}
                          </div>

                          {/* Depois */}
                          {ctxDepois ? (
                            <div className={`p-3 rounded-lg border-l-2 border-gray-400 ${darkMode ? 'bg-zinc-700/50' : 'bg-gray-50'}`}>
                              <p className={`text-xs mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                ⬇️ Mensagem seguinte · {formatDate(ctxDepois.data)}
                              </p>
                              {ctxDepois.msg && <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>📩 {ctxDepois.msg}</p>}
                              {ctxDepois.resp && <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>↩️ {ctxDepois.resp}</p>}
                            </div>
                          ) : (
                            <p className={`text-xs py-2 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}>⬇️ Fim da conversa (até agora)</p>
                          )}
                        </div>

                        <a href={`/atendimento?telefone=${c.telefone}&instancia=${c.instancia_id}`}
                          className={`inline-flex items-center gap-1.5 text-sm mt-4 px-3 py-1.5 rounded-lg font-medium transition-colors ${darkMode ? 'bg-blue-900/30 text-blue-400 hover:bg-blue-900/50' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}>
                          <ArrowRight className="w-4 h-4" /> Ver conversa completa
                        </a>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GraficoSentimento;
